import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useState } from "react";
import { useSearchParams } from "next/navigation";
import Link from "next/link";
import { TextField, Alert, Typography, Box, useTheme, useMediaQuery, InputAdornment, IconButton } from "__barrel_optimize__?names=Alert,Box,IconButton,InputAdornment,TextField,Typography,useMediaQuery,useTheme!=!@mui/material";
import { signIn, signOut } from "next-auth/react";
import ReusableButton from "../../../application/components/ReusableButton";
import { tokens } from "../../../application/theme";
import { useTranslation } from "../../../../i18n/client";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import ArrowBackRounded from "@mui/icons-material/ArrowBackRounded";
import { AuthApi } from "../../../../../apis/auth";
import { isAxiosError } from "axios";
export const LoginForm = (props)=>{
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [message, setMessage] = useState("");
    const [messageType, setMessageType] = useState("info");
    const [emailError, setEmailError] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isGoogleSubmitting, setIsGoogleSubmitting] = useState(false);
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const searchParams = useSearchParams();
    const search = searchParams === null || searchParams === void 0 ? void 0 : searchParams.get("callbackUrl");
    const authError = searchParams === null || searchParams === void 0 ? void 0 : searchParams.get("authError");
    const nextAuthError = searchParams === null || searchParams === void 0 ? void 0 : searchParams.get("error");
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const { t } = useTranslation(props.lng, "login");
    useEffect(()=>{
        if (authError === "credentialsRequired") {
            setMessage(t("login_error_credentials_account"));
            setMessageType("error");
        } else if (authError === "googleSignInFailed" || nextAuthError === "AccessDenied") {
            setMessage(t("googleLoginError"));
            setMessageType("error");
        }
    }, [
        authError,
        nextAuthError,
        t
    ]);
    const handleSignIn = async (event)=>{
        event.preventDefault();
        if (isSubmitting) {
            return;
        }
        setIsSubmitting(true);
        setMessage("");
        try {
            await signOut({
                redirect: false
            });
            const response = await signIn("credentials", {
                email: email,
                password: password,
                redirect: false
            });
            if (response === null || response === void 0 ? void 0 : response.error) {
                let translatedMessage = t("generalErrorMessage");
                let severity = "error";
                let shouldRedirectToVerify = false;
                let isAccountLocked = false;
                const checkShouldRedirect = (errorCode, errorMessage)=>{
                    const normalizedCode = errorCode === null || errorCode === void 0 ? void 0 : errorCode.toUpperCase();
                    const normalizedMessage = errorMessage === null || errorMessage === void 0 ? void 0 : errorMessage.toUpperCase();
                    if (normalizedCode === "EMAIL_EXISTS_NOT_VERIFIED") return true;
                    if (normalizedMessage === null || normalizedMessage === void 0 ? void 0 : normalizedMessage.includes("EMAIL_EXISTS_NOT_VERIFIED")) return true;
                    if (normalizedMessage === null || normalizedMessage === void 0 ? void 0 : normalizedMessage.includes("NOT VERIFIED")) return true;
                    if (normalizedMessage === null || normalizedMessage === void 0 ? void 0 : normalizedMessage.includes("VERIFY")) return true;
                    return false;
                };
                const checkAccountLocked = (errorMessage)=>{
                    if (!errorMessage) return false;
                    const normalizedMessage = errorMessage.toLowerCase();
                    return normalizedMessage.includes("account is locked") || normalizedMessage.includes("locked");
                };
                const checkGoogleSignInRequired = (errorMessage)=>{
                    if (!errorMessage) return false;
                    return errorMessage.toLowerCase().includes("sign in with google");
                };
                if (typeof response.error === "string") {
                    const normalizedError = response.error.replace(/^Error:\s*/i, "").trim();
                    try {
                        const parsedError = JSON.parse(normalizedError);
                        const errorMessage = (parsedError === null || parsedError === void 0 ? void 0 : parsedError.message) || "";
                        // Check for 403 / account locked error from NextAuth
                        if (checkGoogleSignInRequired(errorMessage)) {
                            translatedMessage = t("login_error_google_account");
                            severity = "error";
                        } else if (checkAccountLocked(errorMessage)) {
                            translatedMessage = t("login_error_account_locked");
                            severity = "error";
                            isAccountLocked = true;
                        } else if (checkShouldRedirect(parsedError === null || parsedError === void 0 ? void 0 : parsedError.code, parsedError === null || parsedError === void 0 ? void 0 : parsedError.message)) {
                            translatedMessage = t("login_error_not_verified");
                            severity = "info";
                            shouldRedirectToVerify = true;
                        }
                    } catch (e) {
                        if (checkGoogleSignInRequired(normalizedError)) {
                            translatedMessage = t("login_error_google_account");
                            severity = "error";
                        } else if (checkAccountLocked(normalizedError)) {
                            translatedMessage = t("login_error_account_locked");
                            severity = "error";
                            isAccountLocked = true;
                        } else if (checkShouldRedirect(undefined, normalizedError)) {
                            translatedMessage = t("login_error_not_verified");
                            severity = "info";
                            shouldRedirectToVerify = true;
                        }
                    }
                }
                // Only make additional API call if account is not locked and not redirecting to verify
                if (!shouldRedirectToVerify && !isAccountLocked) {
                    try {
                        await AuthApi.logIn({
                            email,
                            password,
                            provider: "credentials"
                        });
                    } catch (apiError) {
                        if (isAxiosError(apiError)) {
                            var _apiError_response, _payloadArray__msg, _payloadArray_, _payloadArray_1, _apiError_response1, _apiError_response2, _apiError_response3;
                            const payload = (_apiError_response = apiError.response) === null || _apiError_response === void 0 ? void 0 : _apiError_response.data;
                            const payloadArray = Array.isArray(payload === null || payload === void 0 ? void 0 : payload.data) ? payload.data : undefined;
                            var _payload_message, _ref, _ref1, _ref2;
                            const errorMessage = (_ref2 = (_ref1 = (_ref = (_payload_message = payload === null || payload === void 0 ? void 0 : payload.message) !== null && _payload_message !== void 0 ? _payload_message : payload === null || payload === void 0 ? void 0 : payload.error) !== null && _ref !== void 0 ? _ref : payload === null || payload === void 0 ? void 0 : payload.msg) !== null && _ref1 !== void 0 ? _ref1 : payloadArray === null || payloadArray === void 0 ? void 0 : (_payloadArray_ = payloadArray[0]) === null || _payloadArray_ === void 0 ? void 0 : (_payloadArray__msg = _payloadArray_.msg) === null || _payloadArray__msg === void 0 ? void 0 : _payloadArray__msg.message) !== null && _ref2 !== void 0 ? _ref2 : payloadArray === null || payloadArray === void 0 ? void 0 : (_payloadArray_1 = payloadArray[0]) === null || _payloadArray_1 === void 0 ? void 0 : _payloadArray_1.message;
                            if (checkGoogleSignInRequired(errorMessage)) {
                                translatedMessage = t("login_error_google_account");
                                severity = "error";
                            } else if (((_apiError_response1 = apiError.response) === null || _apiError_response1 === void 0 ? void 0 : _apiError_response1.status) === 403) {
                                translatedMessage = t("login_error_account_locked");
                                severity = "error";
                                isAccountLocked = true;
                            } else if (((_apiError_response2 = apiError.response) === null || _apiError_response2 === void 0 ? void 0 : _apiError_response2.status) === 401) {
                                translatedMessage = t("login_error_invalid_credentials");
                                severity = "error";
                            } else if ((_apiError_response3 = apiError.response) === null || _apiError_response3 === void 0 ? void 0 : _apiError_response3.data) {
                                var _payloadArray__msg1, _payloadArray_2;
                                var _payload_code, _ref3;
                                const errorCode = (_ref3 = (_payload_code = payload === null || payload === void 0 ? void 0 : payload.code) !== null && _payload_code !== void 0 ? _payload_code : payload === null || payload === void 0 ? void 0 : payload.errorCode) !== null && _ref3 !== void 0 ? _ref3 : payloadArray === null || payloadArray === void 0 ? void 0 : (_payloadArray_2 = payloadArray[0]) === null || _payloadArray_2 === void 0 ? void 0 : (_payloadArray__msg1 = _payloadArray_2.msg) === null || _payloadArray__msg1 === void 0 ? void 0 : _payloadArray__msg1.code;
                                if (checkShouldRedirect(errorCode, errorMessage)) {
                                    translatedMessage = t("login_error_not_verified");
                                    severity = "info";
                                    shouldRedirectToVerify = true;
                                }
                            }
                        }
                    }
                }
                setMessage(translatedMessage);
                setMessageType(severity);
                if (shouldRedirectToVerify) {
                    try {
                        await AuthApi.sendVerificationCode({
                            email
                        });
                    } catch (sendError) {
                        console.error("Error sending verification code:", sendError);
                    }
                    props.handleRequireVerification(email, password, translatedMessage, severity);
                }
                return;
            } else {
                // console.log('Next Public Client Url -> ', `${process.env.NEXT_PUBLIC_CLIENT_URL}/${lng}`)
                // console.log('Response URL -> ', `${process.env.NEXT_PUBLIC_CLIENT_URL}/${lng}/application`);
                if (search) {
                    // router.refresh();
                    // router.prefetch(search);
                    // window.history.pushState(null, '', `?${search}`)
                    // router.push(`${process.env.NEXT_PUBLIC_CLIENT_URL}/${lng}/application`);
                    window.location.replace("".concat(process.env.NEXT_PUBLIC_CLIENT_URL).concat(search));
                // router.push(search)
                } else {
                    window.location.replace("".concat(process.env.NEXT_PUBLIC_CLIENT_URL, "/").concat(props.lng, "/application"));
                // router.refresh();
                // router.push(`${process.env.NEXT_PUBLIC_CLIENT_URL}/${lng}/application`);
                }
            // router.refresh();
            // router.push(`${window.location.origin}/${lng}/application`);
            // router.push(`${process.env.NEXT_PUBLIC_CLIENT_URL}/${lng}/application`)
            }
        } catch (error) {
            console.error("Error during sign in:", error);
            setMessage(t("generalErrorMessage"));
            setMessageType("error");
        } finally{
            setIsSubmitting(false);
        }
    };
    // useEffect(() => {
    //   console.log('Session -> ', session);
    //   console.log('Status -> ', status);
    //   if(status === 'authenticated') {
    //     router.push(`${process.env.NEXT_PUBLIC_CLIENT_URL}/${lng}/application`)
    //   }
    // }, [session])
    const helperText = ()=>{
        return /*#__PURE__*/ _jsx("span", {
            style: {
                cursor: "pointer"
            },
            onClick: ()=>props.hanleForgotPassword(true),
            children: t("forgotPasswordPrompt")
        });
    };
    const handleChangePassword = (event)=>{
        setPassword(event.target.value);
        setMessage("");
    };
    const handleChangeEmail = (event)=>{
        setEmail(event.target.value);
        setMessage("");
        // Check if the email is in the correct format
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        setEmailError(!emailRegex.test(event.target.value));
    };
    const handleGoogleSignIn = async ()=>{
        if (isGoogleSubmitting) {
            return;
        }
        setIsGoogleSubmitting(true);
        setMessage("");
        try {
            await signOut({
                redirect: false
            });
            // Trigger Google login using NextAuth
            await signIn("google", {
                redirect: true,
                // callbackUrl: `${window.location.origin}/${lng}/application`,
                // callbackUrl: "/",
                callbackUrl: search ? "".concat(process.env.NEXT_PUBLIC_CLIENT_URL).concat(search) : "".concat(process.env.NEXT_PUBLIC_CLIENT_URL, "/").concat(props.lng, "/application")
            });
        } catch (error) {
            console.error("Google sign-in error:", error);
            setMessage("Failed to sign in with Google. Please try again.");
            setMessageType("error");
        } finally{
            setIsGoogleSubmitting(false);
        }
    };
    return /*#__PURE__*/ _jsx(Box, {
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        sx: {
            backgroundColor: "#F3F5FD"
        },
        children: /*#__PURE__*/ _jsxs(Box, {
            width: "100%",
            maxWidth: isMobile ? 320 : 501,
            p: "30px",
            borderRadius: "20px",
            sx: {
                backgroundColor: "#FFF"
            },
            children: [
                /*#__PURE__*/ _jsxs(Box, {
                    sx: {
                        display: "grid",
                        gridTemplateColumns: "42px minmax(0, 1fr) 42px",
                        alignItems: "center",
                        gap: "8px",
                        mb: "20px"
                    },
                    children: [
                        /*#__PURE__*/ _jsx(IconButton, {
                            component: Link,
                            href: "/".concat(props.lng),
                            "aria-label": "Back to home",
                            sx: {
                                width: 42,
                                height: 42,
                                color: colors.text.main,
                                backgroundColor: "#FFF",
                                border: "1px solid #DCE3FC",
                                boxShadow: "0 8px 24px rgba(28, 39, 76, 0.08)",
                                "&:hover": {
                                    backgroundColor: "#EEF3FF"
                                }
                            },
                            children: /*#__PURE__*/ _jsx(ArrowBackRounded, {})
                        }),
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "h3",
                            component: "h1",
                            sx: {
                                textAlign: "center",
                                overflowWrap: "break-word"
                            },
                            children: t("login")
                        }),
                        /*#__PURE__*/ _jsx(Box, {})
                    ]
                }),
                /*#__PURE__*/ _jsxs("form", {
                    onSubmit: handleSignIn,
                    style: {
                        width: "100%"
                    },
                    noValidate: true,
                    children: [
                        /*#__PURE__*/ _jsx(TextField, {
                            label: "Email",
                            type: "email",
                            value: email,
                            onChange: handleChangeEmail,
                            variant: "outlined",
                            margin: "normal",
                            fullWidth: true,
                            required: true,
                            error: emailError,
                            helperText: emailError ? t("invalidEmailFormat") : ""
                        }),
                        /*#__PURE__*/ _jsx(TextField, {
                            label: t("password"),
                            type: showPassword ? "text" : "password",
                            value: password,
                            onChange: handleChangePassword,
                            variant: "outlined",
                            margin: "normal",
                            fullWidth: true,
                            required: true,
                            helperText: helperText(),
                            InputProps: {
                                endAdornment: /*#__PURE__*/ _jsx(InputAdornment, {
                                    position: "end",
                                    children: /*#__PURE__*/ _jsx(IconButton, {
                                        "aria-label": showPassword ? t("hidePassword") : t("showPassword"),
                                        onClick: ()=>setShowPassword((prev)=>!prev),
                                        onMouseDown: (event)=>event.preventDefault(),
                                        edge: "end",
                                        children: showPassword ? /*#__PURE__*/ _jsx(VisibilityOff, {}) : /*#__PURE__*/ _jsx(Visibility, {})
                                    })
                                })
                            }
                        }),
                        /*#__PURE__*/ _jsx(ReusableButton, {
                            name: t("login"),
                            backgroundColor: colors.button.main,
                            backgroundColorOnHover: colors.button.mainOnHower,
                            color: "#FFF",
                            width: "100%",
                            type: "submit",
                            paddingTop: "20px",
                            paddingButtom: "15px",
                            loading: isSubmitting,
                            allowTextWrap: true
                        }),
                        /*#__PURE__*/ _jsx(Typography, {
                            component: "h1",
                            style: {
                                color: "#515151",
                                textAlign: "center",
                                marginBottom: "15px",
                                textTransform: "uppercase"
                            },
                            children: t("or")
                        }),
                        /*#__PURE__*/ _jsx(ReusableButton, {
                            name: "anmelden mit google",
                            // backgroundColor={colors.button.main} 
                            // backgroundColorOnHover={colors.button.mainOnHower} 
                            startIcon: /*#__PURE__*/ _jsxs("svg", {
                                xmlns: "http://www.w3.org/2000/svg",
                                width: "24",
                                height: "24",
                                viewBox: "0 0 24 24",
                                fill: "none",
                                children: [
                                    /*#__PURE__*/ _jsx("path", {
                                        d: "M22.56 12.25C22.56 11.47 22.49 10.72 22.36 10H12V14.26H17.92C17.66 15.63 16.88 16.79 15.71 17.57V20.34H19.28C21.36 18.42 22.56 15.6 22.56 12.25Z",
                                        fill: "#4285F4"
                                    }),
                                    /*#__PURE__*/ _jsx("path", {
                                        d: "M11.9997 22.9996C14.9697 22.9996 17.4597 22.0196 19.2797 20.3396L15.7097 17.5696C14.7297 18.2296 13.4797 18.6296 11.9997 18.6296C9.13969 18.6296 6.70969 16.6996 5.83969 14.0996H2.17969V16.9396C3.98969 20.5296 7.69969 22.9996 11.9997 22.9996Z",
                                        fill: "#34A853"
                                    }),
                                    /*#__PURE__*/ _jsx("path", {
                                        d: "M5.84 14.0903C5.62 13.4303 5.49 12.7303 5.49 12.0003C5.49 11.2703 5.62 10.5703 5.84 9.91031V7.07031H2.18C1.43 8.55031 1 10.2203 1 12.0003C1 13.7803 1.43 15.4503 2.18 16.9303L5.03 14.7103L5.84 14.0903Z",
                                        fill: "#FBBC05"
                                    }),
                                    /*#__PURE__*/ _jsx("path", {
                                        d: "M11.9997 5.38C13.6197 5.38 15.0597 5.94 16.2097 7.02L19.3597 3.87C17.4497 2.09 14.9697 1 11.9997 1C7.69969 1 3.98969 3.47 2.17969 7.07L5.83969 9.91C6.70969 7.31 9.13969 5.38 11.9997 5.38Z",
                                        fill: "#EA4335"
                                    })
                                ]
                            }),
                            borderColor: "#9BC4F5",
                            color: "#282828",
                            width: "100%",
                            onClick: handleGoogleSignIn,
                            loading: isGoogleSubmitting,
                            allowTextWrap: true
                        }),
                        /*#__PURE__*/ _jsxs(Typography, {
                            component: "p",
                            style: {
                                color: "#515151",
                                textAlign: "center",
                                marginTop: "20px"
                            },
                            children: [
                                t("signupPromptText"),
                                " ",
                                /*#__PURE__*/ _jsx(Link, {
                                    href: "/".concat(props.lng, "/signup"),
                                    style: {
                                        color: colors.button.main,
                                        fontWeight: 600,
                                        textDecoration: "none"
                                    },
                                    children: t("signupPromptAction")
                                })
                            ]
                        }),
                        message && /*#__PURE__*/ _jsx(Alert, {
                            severity: messageType,
                            style: {
                                marginTop: "20px"
                            },
                            children: message
                        })
                    ]
                })
            ]
        })
    });
};
